import _ from "lodash";
import * as Sentry from "@sentry/react";
import { Integrations } from "@sentry/tracing";
import { dedupeIntegration } from "@sentry/integrations";
import { SENTRY_URL } from "store/static";
import { getUserInfofromStorage } from "services/localStorage";
import { match } from "react-router";
import { browserHistory } from "store/history";
import { reactRouterV3Instrumentation } from "./router";
import {
  sentryTraceSampleRate,
  isSentryEnabled,
  isSentryBrowserTracingEnabled,
  env,
  _sentryMetricOptions,
} from "config";

const getTraceSampleRate = () => {
  return sentryTraceSampleRate;
};

let replay = null;

const maskPrivateData = text => {
  // Regular expression to match email addresses
  const emailRegex = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g;

  // Regular expression to match phone numbers (for simplicity, this matches various formats)
  const phoneRegex = /(\+?\d{1,4})?\s?-?\(?\d{3}\)?\s?-?\d{3}\s?-?\d{4}/g;

  // Replace emails and phone numbers with ***
  const maskedText = text.replace(emailRegex, "***").replace(phoneRegex, "***");

  const truncatedText = _.truncate(maskedText, {
    length: 28, // Maximum string length (including omission)
    omission: " ...", // Omission string to indicate truncation
  });

  return truncatedText;
};

const updateSentryBreadcrumbs = ({ breadcrumb, hint }) => {
  const { category = "", message = "", data = {} } = breadcrumb;

  let customMessage = message;
  let customData = data;

  switch (category) {
    case "xhr":
      break;
    case "fetch":
      {
        const THRESHOLD = 500; // Threshold for the JSON string length

        const { input } = hint;

        // input[0] => url, input[1] => operationData
        const operationsData = JSON.parse(_.get(input[1], "body", null));

        const operations = _.map(operationsData, operation => {
          // If operation is an empty object, return null
          if (_.isEmpty(operation)) {
            return null;
          }

          const { operationName, variables } = operation;

          // If operationName or variables undefined, return null
          if (_.isNil(operationName) || _.isNil(variables)) {
            return null;
          }

          const operationStringSize = _.size(JSON.stringify(variables));
          return {
            operationName,
            variables:
              operationStringSize <= THRESHOLD ? variables : "Too large",
          };
        });

        const filteredOperations = _.compact(operations);

        if (!_.isEmpty(filteredOperations)) {
          customData = {
            ...customData,
            operations: filteredOperations,
          };
        }
      }
      break;
    case "ui.click":
      {
        const { event } = hint;
        customMessage = `click > ${event?.target?.localName}`;

        const textOnElement = maskPrivateData(
          event?.target?.textContent || event?.target?.placeholder
        );

        customData = {
          ...customData,
          htmlElement: event?.target?.localName,
          tagName: event?.target?.tagName,
          placeholder: event?.target?.placeholder,
          className: event?.target?.className,
          testId: event?.target?.dataset?.testid,
        };

        if (!_.isEmpty(textOnElement) && !_.isNil(textOnElement)) {
          customMessage += ` (${textOnElement})`;
          customData.textContent = textOnElement;
        }
      }
      break;
    case "ui.input":
      {
        const { event } = hint;

        customMessage = `input > ${event?.target?.localName}`;
        const textOnElement =
          event?.target?.textContent || event?.target?.placeholder;
        if (!_.isEmpty(textOnElement)) {
          customMessage += ` > ${textOnElement}`;
        }

        customData = {
          ...customData,
          htmlElement: event?.target?.localName,
          tagName: event?.target?.tagName,
          placeholder: event?.target?.placeholder,
          className: event?.target?.className,
          testId: event?.target?.dataset?.testid,
        };
      }
      break;
    case "navigation":
      break;
    default:
      {
        customMessage = `Unknown category - ${category}: ${message}`;
      }
      break;
  }

  return {
    updatedBreadcrumb: {
      ...breadcrumb,
      message: customMessage,
      data: customData,
    },
  };
};

export const initSentry = ({ plainRoutes }) => {
  // const plainRoutes = Router.createRoutes(routes);

  if (!isSentryEnabled) {
    return;
  }

  const integrations = [dedupeIntegration()];

  if (isSentryBrowserTracingEnabled) {
    integrations.push(
      new Integrations.BrowserTracing({
        tracePropagationTargets: ["localhost", ".toddleapp.com"],
        routingInstrumentation: reactRouterV3Instrumentation(
          browserHistory,
          // Must be Plain Routes.
          plainRoutes,
          match
        ),
        _metricOptions: _sentryMetricOptions,
        enableInp: true,
      })
    );
  }

  Sentry.init({
    dsn: SENTRY_URL,
    environment: env,
    allowUrls: [".toddleapp.com"],
    ignoreErrors: [
      "ChunkLoadError",
      /Loading chunk/i,
      /Failed to execute 'pushState' on 'History'/i,
      /Failed to execute 'send' on 'XMLHttpRequest'/i,
      /Failed to execute 'setStart' on 'Range'/i,
      /Non-Error exception captured with keys/i,
    ],
    replaysSessionSampleRate: 1,
    integrations,
    tracesSampleRate: getTraceSampleRate(),
    normalizeDepth: 10,
    debug: false,
    beforeBreadcrumb(breadcrumb, hint) {
      if (breadcrumb.category == "console") {
        return null;
      }
      const result = updateSentryBreadcrumbs({ breadcrumb, hint });
      return result?.updatedBreadcrumb;
    },
    beforeSend(event, hint) {
      const error = hint.originalException;
      const errorMessage = _.get(error, "message", "");
      const userInfo = getUserInfofromStorage();

      // Exclude ApolloClient errors

      const exceptionValue = _.last(_.get(event, "exception.values", []));
      const stacktraceFrames = _.get(exceptionValue, "stacktrace.frames", []);
      const lastStacktraceFrame = _.last(stacktraceFrames);

      if (
        _.includes(
          lastStacktraceFrame?.filename,
          "./node_modules/@apollo/client"
        )
      ) {
        return null;
      }

      /**
       * @description filter 'JSON syntax error' only for user 121591
       * @link https://sentry.io/organizations/teacher-tools/issues/2241224291/events/9f43a778812342e29cd3f040a3dadb7f/?project=1330341
       */
      if (
        _.get(userInfo, "id", null) == "121591" &&
        errorMessage.match(/JSON syntax error/i)
      ) {
        //console.info("JSON syntax error : Error dropped from sentry report.");
        return null;
      }
      return event;
    },
  });
};

const startSessionRecording = async () => {
  if (!isSentryEnabled) return;
  if (replay) {
    replay.start();
  } else {
    replay = new Sentry.Replay({
      maskAllText: false,
      blockAllMedia: false,
      maskAllInputs: false,
      stickySession: false,
    });
    Sentry.getCurrentHub().getClient().addIntegration(replay);
  }
};

const stopSessionRecording = async () => {
  if (!isSentryEnabled) return;
  await replay.stop();
  // .then(() => {
  //   console.log("Recording has been stopped");
  // })
  // .catch(e => {
  //   console.log("Error while stopping recording : ", e);
  // });
  await replay.flush();
  // .then(() => {
  //   console.log("Recording has been flushed");
  // })
  // .catch(e => {
  //   console.log("Error while flushing recording : ", e);
  // });
};

export { Sentry, startSessionRecording, stopSessionRecording };
